/**
 * The calculations a report can add, and helpers for their formulas.
 * Pure functions, no React.
 */
import { parseFormula } from '../../../../shared/reportFormula';

/** The calculations a report can add, as offered in the Calculations menu. */
export const CALCULATIONS = [
  { type: 'percentOfTotal', name: 'Percent of total', hint: 'Each row\'s share of the total, such as each account\'s share of gross profit.', suffix: '% of total' },
  { type: 'runningTotal', name: 'Running total', hint: 'Adds up the values row by row. With a date breakdown it adds up over time.', suffix: 'running total' },
  { type: 'change', name: 'Change from previous row', hint: 'The difference from the row before. With a date breakdown, the change from the previous period.', suffix: 'change' },
  { type: 'percentChange', name: '% change from previous row', hint: 'The change from the row before as a percentage, such as month-on-month growth.', suffix: '% change' },
  { type: 'formula', name: 'Custom formula', hint: 'Your own sum using the values, such as [Gross profit] / [Turnover].' },
];

export const calculationInfo = (type) => CALCULATIONS.find((c) => c.type === type);

/** The fields a formula can use: the report's values, and the calculations listed before it. */
export function formulaFields(form, calc, labelOf) {
  const measures = form.measures.map((m) => ({ name: labelOf(m), key: m }));
  const before = [];
  for (const c of form.calculations) {
    if (c.id === calc.id) break;
    before.push({ name: c.label, key: `calc:${c.id}` });
  }
  return [...measures, ...before];
}

/** The formula's field names mapped to what they refer to, so a renamed field does not break it. */
export function formulaRefs(formula, fields, previous = {}) {
  const parsed = parseFormula(formula);
  if (!parsed.ok) return {};
  const byName = new Map(fields.map((f) => [f.name.toLowerCase(), f.key]));
  const keys = new Set(fields.map((f) => f.key));
  // A name kept from before still counts while what it named is in the report.
  return Object.fromEntries(parsed.fields
    .map((n) => [n, byName.get(n.toLowerCase()) || (keys.has(previous[n]) ? previous[n] : undefined)])
    .filter(([, k]) => k));
}

/** A one-line description of a calculation for its chip. */
export function describeCalculation(calc, labelOf) {
  if (calc.type === 'formula') return calc.formula || 'No formula yet';
  return `${calculationInfo(calc.type)?.name || calc.type} of ${calc.measure ? labelOf(calc.measure) : '…'}`;
}
